import { useRef, useState } from "react";
import type { EventType, ImportCommitResult, ImportPreviewEvent, Person, Source } from "@shared/types";
import { Modal } from "./ui/Modal";
import { Button } from "./ui/Button";
import { importApi, ApiError } from "@/api/client";
import { formatDateTimeRange } from "@/utils/date";

interface ImportDialogProps {
  open: boolean;
  people: Person[];
  eventTypes: EventType[];
  sources: Source[];
  onClose: () => void;
  onImported: () => void;
}

type Step = "select" | "configure" | "report";
type ItemAction = "import" | "skip" | "overwrite";
type SourceMode = "existing" | "new";

interface PreviewItem extends ImportPreviewEvent {
  action: ItemAction;
}

/** A UID match means the source system's own stable ID (e.g. a GotSport match number)
 * confirmed this is the same game — safe to auto-refresh with the latest data, which is what
 * makes re-importing a few times a week actually pick up schedule/score changes without
 * clicking "Overwrite" on every row by hand. A heuristic (title/time/location) match is less
 * certain — it could coincidentally match a manually-added event — so that stays a manual
 * per-row decision, defaulting to Skip. */
function defaultActionFor(event: ImportPreviewEvent): ItemAction {
  if (!event.isDuplicate) return "import";
  if (event.duplicateMatchType === "uid" && event.existingEventId) return "overwrite";
  return "skip";
}

/** Diacritic/case-insensitive comparison — GotSport's .xlsx text drops accents (e.g. "Barca"
 * for "Barça"), so a plain substring match against our own source labels would miss. */
function normalizeForMatch(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

export function ImportDialog({ open, people, eventTypes, sources, onClose, onImported }: ImportDialogProps) {
  const [step, setStep] = useState<Step>("select");
  const [filename, setFilename] = useState("");
  const [detectedTeamName, setDetectedTeamName] = useState<string | null>(null);
  const [durationMinutes, setDurationMinutes] = useState(90);
  const [items, setItems] = useState<PreviewItem[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [eventTypeId, setEventTypeId] = useState("");
  const [sourceMode, setSourceMode] = useState<SourceMode>(sources.length > 0 ? "existing" : "new");
  const [selectedSourceId, setSelectedSourceId] = useState("");
  const [sourceLabel, setSourceLabel] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<ImportCommitResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function reset() {
    setStep("select");
    setDetectedTeamName(null);
    setDurationMinutes(90);
    setItems([]);
    setParseErrors([]);
    setParticipantIds([]);
    setEventTypeId("");
    setSourceMode(sources.length > 0 ? "existing" : "new");
    setSelectedSourceId("");
    setSourceLabel("");
    setSourceUrl("");
    setError(null);
    setReport(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleFileChosen(file: File) {
    setLoading(true);
    setError(null);
    const fileIsXlsx = /\.xlsx$/i.test(file.name);
    try {
      const result = await importApi.preview(file, fileIsXlsx ? durationMinutes : undefined);
      setFilename(result.filename);
      setDetectedTeamName(result.detectedTeamName ?? null);
      setSourceLabel(result.detectedTeamName || result.filename.replace(/\.(ics|xlsx)$/i, ""));
      setParseErrors(result.parseErrors);
      setItems(result.events.map((e) => ({ ...e, action: defaultActionFor(e) })));

      // Re-derive from the current `sources` prop rather than trusting whatever sourceMode
      // was initialized with — this dialog stays mounted for the app's whole lifetime, so an
      // import right after a fresh page load would otherwise be stuck with whatever sources
      // had (or hadn't) loaded yet at that initial render.
      const matchedSource = result.detectedTeamName
        ? sources.find((s) => normalizeForMatch(s.label).includes(normalizeForMatch(result.detectedTeamName!)))
        : undefined;
      if (matchedSource) {
        setSourceMode("existing");
        setSelectedSourceId(matchedSource.id);
      } else {
        setSourceMode(sources.length > 0 ? "existing" : "new");
      }

      setStep("configure");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not read that file");
    } finally {
      setLoading(false);
    }
  }

  function toggleParticipant(id: string) {
    setParticipantIds((ids) => (ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]));
  }

  function setItemAction(uid: string, action: ItemAction) {
    setItems((list) => list.map((item) => (item.uid === uid ? { ...item, action } : item)));
  }

  async function handleCommit() {
    if (participantIds.length === 0) return setError("Choose at least one participant");
    if (!eventTypeId) return setError("Choose a default event type");
    if (sourceMode === "existing" && !selectedSourceId) return setError("Choose a source, or switch to \"New source\"");
    setLoading(true);
    setError(null);
    try {
      const result = await importApi.commit({
        filename,
        participantIds,
        eventTypeId,
        sourceId: sourceMode === "existing" ? selectedSourceId : null,
        sourceLabel: sourceLabel || filename,
        sourceUrl: sourceUrl || null,
        events: items.map((item) => ({
          uid: item.uid,
          title: item.title,
          description: item.description,
          location: item.location,
          opponent: item.opponent,
          team: item.team,
          startAt: item.startAt,
          endAt: item.endAt,
          allDay: item.allDay,
          action: item.action,
          existingEventId: item.existingEventId,
        })),
      });
      setReport(result);
      setStep("report");
      onImported();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Import failed");
    } finally {
      setLoading(false);
    }
  }

  const importCount = items.filter((i) => i.action === "import").length;
  const overwriteCount = items.filter((i) => i.action === "overwrite").length;
  const skipCount = items.filter((i) => i.action === "skip").length;

  return (
    <Modal open={open} onClose={handleClose} width="lg">
      <div className="flex max-h-[90vh] flex-col">
        <div className="flex items-center justify-between border-b border-line dark:border-line-dark px-6 py-4">
          <h2 className="text-lg font-semibold">Import schedule</h2>
          <button onClick={handleClose} aria-label="Close" className="rounded-full p-1.5 hover:bg-black/5 dark:hover:bg-white/10">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === "select" && (
            <div className="flex flex-col items-center justify-center gap-4 py-10 text-center">
              <p className="text-sm text-muted dark:text-muted-dark">
                Choose an .ics file (AssignRefs, most calendars) or a GotSport schedule export
                (.xlsx — from a team's "Export Schedule" button).
              </p>
              <div className="flex items-center gap-2 text-sm">
                <label className="text-muted dark:text-muted-dark">Default game length</label>
                <input
                  type="number"
                  min={15}
                  step={15}
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(Number(e.target.value) || 90)}
                  className="w-16 rounded-lg border border-line dark:border-line-dark bg-transparent px-2 py-1 text-center"
                />
                <span className="text-muted dark:text-muted-dark">minutes (used for .xlsx imports only)</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".ics,.xlsx,text/calendar,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileChosen(file);
                }}
                className="text-sm"
              />
              {loading && <p className="text-sm text-muted dark:text-muted-dark">Reading file…</p>}
              {error && <p className="text-sm font-medium text-red-600">{error}</p>}
            </div>
          )}

          {step === "configure" && (
            <div className="space-y-5">
              {detectedTeamName && (
                <div className="rounded-lg bg-accent/10 p-3 text-sm text-accent">
                  Detected team: <span className="font-semibold">{detectedTeamName}</span> — double-check the
                  participant(s) and opponents below look right before importing.
                </div>
              )}
              {parseErrors.length > 0 && (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-200">
                  {parseErrors.join(" ")}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-muted dark:text-muted-dark">Participant(s)</label>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {people.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => toggleParticipant(p.id)}
                        className="rounded-full border px-2.5 py-1 text-xs font-medium"
                        style={
                          participantIds.includes(p.id)
                            ? { backgroundColor: p.color, borderColor: p.color, color: "white" }
                            : {}
                        }
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted dark:text-muted-dark">Default event type</label>
                  <select
                    value={eventTypeId}
                    onChange={(e) => setEventTypeId(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm"
                  >
                    <option value="" disabled>
                      Choose a type
                    </option>
                    {eventTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-muted dark:text-muted-dark">Source</label>
                  <div className="mt-1 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSourceMode("existing")}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${sourceMode === "existing" ? "border-accent bg-accent/10 text-accent" : "border-line dark:border-line-dark text-muted dark:text-muted-dark"}`}
                      disabled={sources.length === 0}
                    >
                      Existing source
                    </button>
                    <button
                      type="button"
                      onClick={() => setSourceMode("new")}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${sourceMode === "new" ? "border-accent bg-accent/10 text-accent" : "border-line dark:border-line-dark text-muted dark:text-muted-dark"}`}
                    >
                      New source
                    </button>
                  </div>

                  {sourceMode === "existing" ? (
                    <select
                      value={selectedSourceId}
                      onChange={(e) => setSelectedSourceId(e.target.value)}
                      className="mt-2 w-full rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm"
                    >
                      <option value="" disabled>
                        Choose a source
                      </option>
                      {sources.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <input
                        value={sourceLabel}
                        onChange={(e) => setSourceLabel(e.target.value)}
                        placeholder="Source label"
                        className="rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm"
                      />
                      <input
                        value={sourceUrl}
                        onChange={(e) => setSourceUrl(e.target.value)}
                        placeholder="Source URL (optional)"
                        className="rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-medium text-muted dark:text-muted-dark">
                  {items.length} events found · {importCount} to import · {overwriteCount} to overwrite ·{" "}
                  {skipCount} to skip
                </p>
                <div className="max-h-80 overflow-y-auto rounded-lg border border-line dark:border-line-dark divide-y divide-line dark:divide-line-dark">
                  {items.map((item) => (
                    <div key={item.uid} className="flex items-center justify-between gap-3 px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{item.title}</p>
                        <p className="truncate text-xs text-muted dark:text-muted-dark">
                          {formatDateTimeRange(item.startAt, item.endAt, item.allDay)}
                          {item.location ? ` · ${item.location}` : ""}
                        </p>
                        {item.isDuplicate && item.duplicateMatchType === "uid" && (
                          <p className="mt-0.5 text-xs font-medium text-accent">↻ Already on the calendar — will refresh with this data</p>
                        )}
                        {item.isDuplicate && item.duplicateMatchType !== "uid" && (
                          <p className="mt-0.5 text-xs font-medium text-amber-600">⚠ {item.duplicateReason} — review before overwriting</p>
                        )}
                      </div>
                      <select
                        value={item.action}
                        onChange={(e) => setItemAction(item.uid, e.target.value as ItemAction)}
                        className="shrink-0 rounded-lg border border-line dark:border-line-dark bg-transparent px-2 py-1 text-xs"
                      >
                        <option value="import">Import</option>
                        <option value="skip">Skip</option>
                        {item.isDuplicate && item.existingEventId && (
                          <option value="overwrite">Overwrite</option>
                        )}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {error && <p className="text-sm font-medium text-red-600">{error}</p>}
            </div>
          )}

          {step === "report" && report && (
            <div className="space-y-4 py-4 text-center">
              <p className="text-3xl">✅</p>
              <p className="text-lg font-semibold">Import complete</p>
              <div className="mx-auto grid max-w-sm grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-2xl font-semibold tabular-nums">{report.imported}</p>
                  <p className="text-xs text-muted dark:text-muted-dark">Imported</p>
                </div>
                <div>
                  <p className="text-2xl font-semibold tabular-nums">{report.overwritten}</p>
                  <p className="text-xs text-muted dark:text-muted-dark">Overwritten</p>
                </div>
                <div>
                  <p className="text-2xl font-semibold tabular-nums">{report.skipped}</p>
                  <p className="text-xs text-muted dark:text-muted-dark">Skipped</p>
                </div>
              </div>
              {report.errors.length > 0 && (
                <div className="mx-auto max-w-sm rounded-lg bg-red-50 dark:bg-red-500/10 p-3 text-left text-xs text-red-700 dark:text-red-300">
                  {report.errors.map((e) => (
                    <p key={e.uid}>{e.message}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-line dark:border-line-dark px-6 py-4">
          {step === "configure" && (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleCommit} disabled={loading}>
                {loading ? "Importing…" : `Import ${importCount + overwriteCount} events`}
              </Button>
            </>
          )}
          {step === "report" && (
            <Button variant="primary" onClick={handleClose}>
              Done
            </Button>
          )}
          {step === "select" && (
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
